import React, { createContext, useContext, useState, useEffect } from 'react';
import { MARKET_REFRESH_MS, authAPI, resolveAssetUrl } from '../services/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('ipl_token');
    if (token) {
      authAPI.me()
        .then(r => setUser({ ...r.data, avatar_url: resolveAssetUrl(r.data.avatar_url) }))
        .catch(() => localStorage.removeItem('ipl_token'))
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user) return undefined;

    const interval = setInterval(() => {
      authAPI.me()
        .then(r => setUser(prev => ({ ...prev, ...r.data, avatar_url: resolveAssetUrl(r.data.avatar_url) })))
        .catch(() => {});
    }, MARKET_REFRESH_MS);

    return () => clearInterval(interval);
  }, [user]);

  const login = async (email, password) => {
    const r = await authAPI.login({ email, password });
    localStorage.setItem('ipl_token', r.data.token);
    setUser({ ...r.data.user, avatar_url: resolveAssetUrl(r.data.user.avatar_url) });
    return r.data;
  };

  const register = async (username, email, password) => {
    const r = await authAPI.register({ username, email, password });
    localStorage.setItem('ipl_token', r.data.token);
    setUser({ ...r.data.user, avatar_url: resolveAssetUrl(r.data.user.avatar_url) });
    return r.data;
  };

  const loginWithGoogle = async (credential) => {
    const r = await authAPI.googleSignIn(credential);
    localStorage.setItem('ipl_token', r.data.token);
    setUser({ ...r.data.user, avatar_url: resolveAssetUrl(r.data.user.avatar_url) });
    return r.data;
  };

  const logout = () => {
    localStorage.removeItem('ipl_token');
    sessionStorage.removeItem('rules_shown');
    setUser(null);
  };

  // Called after a trade to sync balance + trade count from server response
  const updateAfterTrade = ({ newBalance, trade_count, trades_remaining }) => {
    setUser(u => ({
      ...u,
      points: newBalance,
      trade_count: trade_count ?? u.trade_count,
      trades_remaining: trades_remaining ?? u.trades_remaining,
    }));
  };

  // Legacy: just update points
  const updatePoints = (points) => setUser(u => ({ ...u, points }));

  const updateUser = (patch) => {
    setUser(u => ({
      ...u,
      ...(typeof patch === 'function' ? patch(u) : patch),
    }));
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, loginWithGoogle, logout, updatePoints, updateAfterTrade, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
